/**
 * Story 2.51: helper de ordenação alfabética PT-BR.
 *
 * Centraliza o pattern usado em hooks/components que exibem listas em
 * comboboxes/dropdowns (Cliente, Hospital, Auditor, Usuário, etc).
 *
 * Sempre case-insensitive + accent-insensitive (sensitivity: 'base').
 * Retorna nova array (não muta original).
 */

/**
 * Resolve o "display name" de um item escolhendo o primeiro field não-vazio.
 *
 * Ex:  resolveDisplayName(cliente, ['nome_fantasia', 'razao_social'])
 *      → 'Hospital Santa Casa' se nome_fantasia preenchido,
 *      → 'SANTA CASA MISERICÓRDIA' se só razao_social preenchido,
 *      → '' se nenhum.
 */
export function resolveDisplayName<T extends Record<string, unknown>>(
  item: T,
  fields: (keyof T)[],
): string {
  for (const f of fields) {
    const v = item[f];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return '';
}

/**
 * Ordena array alfabeticamente pelo primeiro field não-vazio.
 *
 * Uso:
 *   sortByAlpha(clientes, ['nome_fantasia', 'razao_social'])
 *   sortByAlpha(hospitais, ['nome_fantasia', 'razao_social'])
 *   sortByAlpha(auditores, ['nome'])
 *   sortByAlpha(usuarios, ['first_name', 'username'])
 */
export function sortByAlpha<T extends Record<string, unknown>>(
  items: T[],
  fields: (keyof T)[],
): T[] {
  return [...items].sort((a, b) => {
    const labelA = resolveDisplayName(a, fields);
    const labelB = resolveDisplayName(b, fields);
    return labelA.localeCompare(labelB, 'pt-BR', { sensitivity: 'base' });
  });
}
